import { SearchOutlined } from 'components/icons';
import NestedTableSortableHeader from 'components/tables/list-table/NestedTableSortableHeader';
import { Checkbox } from 'components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { ReactNode } from 'react';
import { CalculatedPricelist } from 'types/pricelists';
import { t } from 'utils/getTranslations';
import { isCurrentlyValid } from './employeePricing';

// Narrowings the user can switch on beside the search box. Each stands on its own and they combine,
// so the state is the set of ticked keys rather than one selected value.
export const QUICK_FILTERS: {
    value: string;
    labelKey: string;
    isDefault?: boolean;
    matches: (pricelist: CalculatedPricelist) => boolean;
}[] = [
    {
        value: 'current',
        labelKey: 'pricelists.picker.only-current',
        // Prefilling from a list that has run out or has not started yet is the rare case, so the picker
        // opens narrowed to the ones in force and the user unticks to reach the others.
        isDefault: true,
        // A list carries one validity label beside its source label; current means in force today, so
        // this drops the ones that have run out and the ones that have not started.
        matches: isCurrentlyValid,
    },
];

export const DEFAULT_QUICK_FILTERS = QUICK_FILTERS.filter((filter) => filter.isDefault).map((filter) => filter.value);

// Name search text, the `name,asc|desc` sort token (empty = the picker's curated scope order) and the
// ticked quick filters.
export interface PickerViewOptions {
    search: string;
    sort: string;
    filters: string[];
}

interface Props {
    viewOptions: PickerViewOptions;
    onChange: (next: PickerViewOptions) => void;
}

/**
 * Sticks to the top of the card list so the controls stay reachable however far the cards are scrolled.
 * `z-2` is local to the card list: it only has to clear the cards scrolling under it.
 */
export const PricelistsPickerToolbar = ({ viewOptions, onChange }: Props): ReactNode => (
    <div className="bg-paper sticky top-0 z-2 pb-2">
        <div className="flex items-center gap-3">
            <InputGroup className="bg-paper flex-1">
                <InputGroupAddon>
                    <SearchOutlined className="text-grey-500 size-3.5" />
                </InputGroupAddon>
                <InputGroupInput
                    value={viewOptions.search}
                    onChange={(event) => onChange({ ...viewOptions, search: event.target.value })}
                    placeholder={t('search')}
                    aria-label={t('search')}
                    className="text-base md:text-xs"
                />
            </InputGroup>
            <NestedTableSortableHeader
                columnId="name"
                label={t('pricelists.name')}
                sort={viewOptions.sort}
                onSortChange={(next) => onChange({ ...viewOptions, sort: next })}
                ascFirst
            />
        </div>
        {/* Checkboxes rather than pressed pills, so the picker's narrowings read the same as the quick
            filters above every list table. */}
        <div className="flex flex-wrap items-center gap-x-3 pt-1.5">
            {QUICK_FILTERS.map((filter) => (
                <label key={filter.value} className="flex min-h-7 cursor-pointer items-center gap-2 px-1">
                    <Checkbox
                        variant="secondary"
                        checked={viewOptions.filters.includes(filter.value)}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...viewOptions,
                                filters: checked
                                    ? [...viewOptions.filters, filter.value]
                                    : viewOptions.filters.filter((value) => value !== filter.value),
                            })
                        }
                    />
                    <span className="text-grey-700 text-[0.78rem]">{t(filter.labelKey)}</span>
                </label>
            ))}
        </div>
    </div>
);
